import * as React from 'react'
import * as formik from 'formik'

import * as plume from '@ulule/owl-kit-components/next'

import { usePrevious } from './usePrevious'

export function useScrollToFirstErrorFunction<T>(formikBag: formik.FormikProps<T>): () => void {
  const shouldScrollRef = React.useRef(false)
  React.useEffect(() => {
    if (formikBag.isSubmitting) {
      shouldScrollRef.current = true
    }
  }, [formikBag.isSubmitting])

  return () => {
    if (shouldScrollRef.current) {
      // I know what you are thinking. You are frowning. Maybe a
      // disgusted pout. "`querySelector` in a React project? This is a
      // code smell!". Well yeah but for this use case, it is also a lot
      // simpler and less brittle than the "correct" approach with refs.
      // When a field is in error, a special className is added, so we
      // can `querySelector` the first field in error in order to scroll
      // to it.
      const firstErrorField = document.querySelector<HTMLElement>(plume.ERROR_STATUS_SELECTOR)
      firstErrorField?.scrollIntoView({ behavior: 'smooth' })
      shouldScrollRef.current = false
    }
  }
}

export function useScrollToFirstError<T>(formikBag: formik.FormikProps<T>): void {
  const scrollToFirstError = useScrollToFirstErrorFunction(formikBag)

  // We want to wait after formik is done submitting, so a single `usePrevious`
  // should be enough, but some errors can be set in response to a submit
  // failure. Those are set on the same render as `isSubmitting` changes to
  // `false`. The `useModelTranslation` hook do not have time to rerender and
  // compute the new list of lang errors. Using two `usePrevious` makes this
  // hook pick on all the errors, including those set after submitting is done.
  const wasWasSubmitting = usePrevious(usePrevious(formikBag.isSubmitting)) ?? false

  React.useEffect(() => {
    if (!formikBag.isSubmitting) {
      scrollToFirstError()
    }
  }, [wasWasSubmitting])
}
